import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating WhatsApp support button.
 *
 * - Reuses `show.whatsapp_number` (the existing buyer-contact field).
 * - Reads new opt-in flag `show.whatsapp_float_enabled` plus the message and
 *   tooltip texts the admin configured.
 * - Hidden on admin routes and on routes that don't make sense for support
 *   (e.g. the public display screen kiosks).
 */
export const FloatingWhatsAppButton = () => {
  const [show, setShow] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/show`).then((r) => { if (!cancelled) setShow(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Auto-show tooltip 1.5s after mount, hide after 6s. Only first time per session.
  useEffect(() => {
    if (!show?.whatsapp_float_enabled) return;
    const seen = sessionStorage.getItem("wa_float_seen");
    if (seen) return;
    const t1 = setTimeout(() => setTooltipOpen(true), 1500);
    const t2 = setTimeout(() => { setTooltipOpen(false); sessionStorage.setItem("wa_float_seen", "1"); }, 7500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show?.whatsapp_float_enabled]);

  // Hide on admin routes and other non-public surfaces
  const path = location.pathname || "";
  const hidden = path.startsWith("/admin") || path.startsWith("/display") || path.startsWith("/backstage-display");
  if (hidden) return null;
  // Also hide when the alternate "inactive landing" is being rendered — the
  // simple bio-link page should stay uncluttered.
  const inactivePreview = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("inactive_preview") === "1";
  if (show?.inactive_landing_enabled || inactivePreview) return null;
  if (!show?.whatsapp_float_enabled) return null;

  const raw = (show.whatsapp_number || "").trim();
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;

  const baseMsg = show.whatsapp_float_message || "Olá, tenho uma dúvida.";
  const msg = baseMsg.replace(/\{evento\}/g, show.name || "").replace(/\{nome\}/g, show.name || "");
  const href = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
  const tooltip = show.whatsapp_float_tooltip || "Em caso de dúvida envie mensagem";

  return (
    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 pointer-events-none" data-testid="whatsapp-float-container">
      {tooltipOpen && (
        <div
          className="pointer-events-auto bg-white text-zinc-900 text-sm font-medium px-3 py-2 rounded-lg shadow-xl max-w-[230px] animate-in fade-in slide-in-from-right-2 duration-300"
          onClick={() => setTooltipOpen(false)}
          role="button"
          data-testid="whatsapp-float-tooltip"
        >
          {tooltip}
          <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rotate-45" />
        </div>
      )}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="pointer-events-auto relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebd5b] shadow-2xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
        onMouseEnter={() => setTooltipOpen(true)}
        onClick={() => sessionStorage.setItem("wa_float_seen", "1")}
        aria-label="Contactar via WhatsApp"
        data-testid="whatsapp-float-button"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
        <svg viewBox="0 0 32 32" className="w-7 h-7 fill-white relative z-10" aria-hidden="true">
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-1.21-.832-1.467-.214-.515-.355-.473-.595-.473-.075 0-.15-.007-.225-.007a.86.86 0 0 0-.62.236C11.706 12.236 11.2 13 11.2 14.43c0 1.49 1.043 2.93 1.187 3.13.143.2 2.05 3.225 5.02 4.41 2.49.985 3 .855 3.54.8.66-.07 2.117-.85 2.42-1.69.302-.84.302-1.555.213-1.69-.085-.135-.31-.215-.685-.39-.375-.18-2.213-1.08-2.555-1.2-.345-.12-.595-.18-.845.18-.255.36-.985 1.225-1.21 1.48-.225.24-.45.27-.83.09-.375-.18-1.575-.58-3-1.85-1.11-.99-1.85-2.205-2.07-2.58-.215-.375-.025-.575.165-.76.165-.165.375-.435.555-.65.18-.215.245-.36.36-.6.12-.235.06-.45-.03-.63-.085-.18-.84-2.025-1.155-2.775-.305-.73-.615-.63-.84-.645a16.5 16.5 0 0 0-.715-.013.86.86 0 0 0-.62.236c-.205.225-.79.775-.79 1.89s.81 2.19.92 2.345c.115.155 1.585 2.485 3.84 3.485 2.255 1 2.255.665 2.66.625.405-.045 1.305-.535 1.49-1.055.18-.515.18-.96.125-1.05-.05-.085-.205-.135-.43-.245M16.005 0C7.165 0 0 7.165 0 16c0 2.806.74 5.546 2.145 7.96L.105 31.49a.755.755 0 0 0 .92.925l7.755-2.03A15.94 15.94 0 0 0 16 32c8.835 0 16-7.165 16-16S24.836 0 16.005 0M16 29.86a13.86 13.86 0 0 1-7.075-1.93l-.51-.305-5.265 1.38 1.405-5.13-.33-.535A13.86 13.86 0 0 1 16 2.14c7.65 0 13.86 6.21 13.86 13.86S23.65 29.86 16 29.86" />
        </svg>
      </a>
    </div>
  );
};

export default FloatingWhatsAppButton;
